import React, { useState, useCallback, useMemo, useEffect } from 'react';
import useAuth from '../Auth/hook_useAuth';
import Api from '../Auth/Api';
import { Toast, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import loginAnimation from '../assets/animations/login-animation.json';
import { useAjustes } from '../Context/AjustesContext';

function Login() {
    const navigate = useNavigate();
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [codigo, setCodigo] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [showCopyrightModal, setShowCopyrightModal] = useState(false);
    const [emailCopied, setEmailCopied] = useState(false);
    const [whatsappCopied, setWhatsappCopied] = useState(false);
    const [errors, setErrors] = useState({ codigo: '', password: '' });
    const [shakeFields, setShakeFields] = useState({ codigo: false, password: false });
    const [isAnimated, setIsAnimated] = useState(false);
    const { signin } = useAuth();
    const { ajustes } = useAjustes();

    // Função para construir URL de imagem
    const getImageUrl = useCallback((path) => {
        if (!path) return null;
        if (path.startsWith('http')) {
            return path; // Already a full URL (external like Imgur)
        }
        return `${process.env.REACT_APP_API_URL}/${path}`;
    }, []);

    // Função para gerar variações de cor
    const colorUtils = useMemo(() => {
        const hexToHSL = (hex) => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            if (!result) return { h: 0, s: 0, l: 0 };
            
            let r = parseInt(result[1], 16) / 255;
            let g = parseInt(result[2], 16) / 255;
            let b = parseInt(result[3], 16) / 255;
            
            const max = Math.max(r, g, b), min = Math.min(r, g, b);
            let h, s, l = (max + min) / 2;
            
            if (max === min) {
                h = s = 0;
            } else {
                const d = max - min;
                s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                switch (max) {
                    case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
                    case g: h = ((b - r) / d + 2) / 6; break;
                    case b: h = ((r - g) / d + 4) / 6; break;
                    default: h = 0;
                }
            }
            return { h: h * 360, s: s * 100, l: l * 100 };
        };

        const hslToHex = (h, s, l) => {
            s /= 100;
            l /= 100;
            const a = s * Math.min(l, 1 - l);
            const f = n => {
                const k = (n + h / 30) % 12;
                const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
                return Math.round(255 * color).toString(16).padStart(2, '0');
            };
            return `#${f(0)}${f(8)}${f(4)}`;
        };

        const lighten = (hex, amount) => {
            const { h, s, l } = hexToHSL(hex);
            return hslToHex(h, s, Math.min(100, l + amount));
        };

        const darken = (hex, amount) => {
            const { h, s, l } = hexToHSL(hex);
            return hslToHex(h, s, Math.max(0, l - amount));
        };

        return { lighten, darken };
    }, []);

    // Cores baseadas na cor primária da API
    const themeColors = useMemo(() => {
        const primary = ajustes?.cor_primaria || '#233734';
        const secondary = ajustes?.cor_secundaria || '#384d49';
        
        return {
            primary,
            secondary,
            primaryLight: colorUtils.lighten(primary, 15),
            primaryDark: colorUtils.darken(primary, 10),
            primaryFaded: colorUtils.lighten(primary, 40),
        };
    }, [ajustes?.cor_primaria, ajustes?.cor_secundaria, colorUtils]);

    // Animação de entrada do painel Lottie
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsAnimated(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    // Usar informações de contato dos ajustes do banco de dados
    const emailContato = ajustes?.email_contato;
    const whatsappContato = ajustes?.whatsapp_contato;
    
    // Formatar telefone para exibição (adicionar parênteses e traço)
    const formatarTelefone = (numero) => {
        if (!numero) return '(00) 00000-0000';
        // Remove tudo que não é número
        const apenasNumeros = numero.replace(/\D/g, '');
        // Se for formato internacional (inicia com 55)
        if (apenasNumeros.startsWith('55') && apenasNumeros.length === 13) {
            const ddd = apenasNumeros.substring(2, 4);
            const parte1 = apenasNumeros.substring(4, 9);
            const parte2 = apenasNumeros.substring(9);
            return `(${ddd}) ${parte1}-${parte2}`;
        }
        // Se for formato nacional (11 dígitos)
        if (apenasNumeros.length === 11) {
            const ddd = apenasNumeros.substring(0, 2);
            const parte1 = apenasNumeros.substring(2, 7);
            const parte2 = apenasNumeros.substring(7);
            return `(${ddd}) ${parte1}-${parte2}`;
        }
        return numero;
    };
    
    const telefoneContato = formatarTelefone(whatsappContato);

    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            if (type === 'email') {
                setEmailCopied(true);
                setWhatsappCopied(false);
                setTimeout(() => setEmailCopied(false), 3000);
            } else if (type === 'whatsapp') {
                setWhatsappCopied(true);
                setEmailCopied(false);
                setTimeout(() => setWhatsappCopied(false), 3000);
            }
        } catch (err) {
            console.error('Erro ao copiar:', err);
        }
    };

    // Função para ativar o shake em um campo
    const triggerShake = (field) => {
        setShakeFields(prev => ({ ...prev, [field]: true }));
        setTimeout(() => {
            setShakeFields(prev => ({ ...prev, [field]: false }));
        }, 500);
    };

    // Validação dos campos
    const validateForm = () => {
        let isValid = true;
        const newErrors = { codigo: '', password: '' };

        if (!codigo.trim()) {
            newErrors.codigo = 'Este campo não pode estar vazio';
            triggerShake('codigo');
            isValid = false;
        }

        if (!password.trim()) {
            newErrors.password = 'Este campo não pode estar vazio';
            triggerShake('password');
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Limpar erro ao digitar
    const handleCodigoChange = (e) => {
        setCodigo(e.target.value);
        if (errors.codigo) {
            setErrors(prev => ({ ...prev, codigo: '' }));
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (errors.password) {
            setErrors(prev => ({ ...prev, password: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await Api.post("/login", { codigo, password });
            signin({ token: response.data.token, nome_completo: response.data.nome_completo, nivel: response.data.nivel, foto: response.data.foto, primeiro_login: response.data.primeiro_login, concluiu_aula: response.data.concluiu_aula }, true);

            if (response.data.primeiro_login === 0 || response.data.primeiro_login === null) {
                navigate("/primeiro-login");
            } else {
                if (response.data.nivel === 'Aluno') {
                    navigate("/aluno/dashboard");
                } else {
                    navigate("/dashboard");
                }
            }
            localStorage.setItem("primeiro_login", response.data.primeiro_login);

        } catch (error) {
            setToast({ header: 'Ocorreu um erro!', body: error.response.data.message, type: 'toast-error' })
            setShowToast(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <div 
                className="login-container"
                style={{
                    '--theme-primary': themeColors.primary,
                    '--theme-secondary': themeColors.secondary,
                    '--theme-primary-light': themeColors.primaryLight,
                    '--theme-primary-dark': themeColors.primaryDark,
                    '--theme-primary-faded': themeColors.primaryFaded,
                }}
            >
                {/* Elementos decorativos de fundo */}
                <div className="bg-decoration">
                    <div className="bg-shape bg-shape-1"></div>
                    <div className="bg-shape bg-shape-2"></div>
                    <div className="bg-shape bg-shape-3"></div>
                </div>

                <div className={`login-content ${isAnimated ? 'animated' : ''}`}>
                    <div className="login-animation-container d-none d-lg-flex">
                        <div className="animation-wrapper">
                            <Lottie
                                animationData={loginAnimation}
                                loop={true}
                                className="login-animation"
                            />
                        </div>
                    </div>

                    <div className="login-form-container">
                        <div className="login-header text-center">
                            {ajustes.logo ? (
                                <img
                                    src={getImageUrl(ajustes.logo)}
                                    alt={ajustes.titulo || "Logo"}
                                    className="login-logo"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "./../assets/logo.png";
                                    }}
                                />
                            ) : (
                                <img
                                    src="./../assets/logo.png"
                                    alt="Logo"
                                    className="login-logo"
                                />
                            )}
                            {ajustes.titulo && !ajustes.logo && (
                                <h4 className="login-title">{ajustes.titulo}</h4>
                            )}
                            <p className="login-subtitle">Acesse sua conta para continuar</p>
                        </div>

                        <form onSubmit={handleSubmit} className="login-form" noValidate>
                            <div className="form-group mb-4">
                                <div className={`input-wrapper ${shakeFields.codigo ? 'shake' : ''}`}>
                                    <span className={`input-icon ${errors.codigo ? 'error' : ''}`}>
                                        <i className="bi bi-person-badge"></i>
                                    </span>
                                    <input
                                        id="matricula"
                                        type="text"
                                        className={`form-control custom-input ${errors.codigo ? 'input-error' : ''}`}
                                        placeholder="Matrícula"
                                        value={codigo}
                                        onChange={handleCodigoChange}
                                    />
                                </div>
                                {errors.codigo && (
                                    <div className="error-message">
                                        <i className="bi bi-exclamation-circle me-1"></i>
                                        {errors.codigo}
                                    </div>
                                )}
                            </div>

                            <div className="form-group mb-4">
                                <div className={`input-wrapper ${shakeFields.password ? 'shake' : ''}`}>
                                    <span className={`input-icon ${errors.password ? 'error' : ''}`}>
                                        <i className="bi bi-lock"></i>
                                    </span>
                                    <input
                                        id="senha"
                                        type={showPassword ? "text" : "password"}
                                        className={`form-control custom-input custom-input-password ${errors.password ? 'input-error' : ''}`}
                                        placeholder="Senha"
                                        value={password}
                                        onChange={handlePasswordChange}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                    >
                                        <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                    </button>
                                </div>
                                {errors.password && (
                                    <div className="error-message">
                                        <i className="bi bi-exclamation-circle me-1"></i>
                                        {errors.password}
                                    </div>
                                )}
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <label className="custom-checkbox">
                                    <input type="checkbox" id="rememberMe" />
                                    <span className="checkmark"></span>
                                    <span className="checkbox-label">Lembrar-me</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() => navigate("/recuperar")}
                                    className="forgot-password-link"
                                >
                                    Esqueceu a senha?
                                </button>
                            </div>

                            <button
                                type="submit"
                                className="btn-login"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Entrando...
                                    </>
                                ) : (
                                    <>
                                        Entrar
                                        <i className="bi bi-arrow-right ms-2"></i>
                                    </>
                                )}
                            </button>

                            {/* Link do Validador */}
                            <div className="text-center mt-4">
                                <button
                                    type="button"
                                    onClick={() => navigate("/validador")}
                                    className="validator-link"
                                >
                                    <i className="bi bi-shield-check me-2"></i>
                                    Validador de Certificados
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="version-info">
                    v{process.env.REACT_APP_API_VERSION}
                </div>

                {/* Footer */}
                <div className="login-footer">
                    <span>Comercializado por{' '}
                    <a 
                        href="https://mfjtreinamentos.com.br" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="footer-link"
                    >
                        MFJ Treinamentos
                    </a></span>
                    <span className="footer-divider">•</span>
                    <button
                        type="button"
                        onClick={() => setShowCopyrightModal(true)}
                        className="footer-link-btn"
                    >
                        © Direitos reservados
                    </button>
                </div>

                {/* Ícone de Suporte */}
                <button 
                    className="support-icon"
                    onClick={() => setShowSupportModal(true)}
                    type="button"
                    title="Suporte"
                >
                    <i className="bi bi-chat-dots"></i>
                    <span className="support-pulse"></span>
                </button>
            </div>

            {/* Modal de Copyright */}
            <Modal show={showCopyrightModal} onHide={() => setShowCopyrightModal(false)} centered>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Plataforma do Grupo MFJ</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <p className="mb-0">
                        Titularidade da 8Bits Tecnologia, com comercialização realizada por MFJ Treinamentos, não havendo subcontratação ou transferência de responsabilidade técnica.
                    </p>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn btn-primary" onClick={() => setShowCopyrightModal(false)}>
                            Fechar
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Modal de Suporte */}
            <Modal show={showSupportModal} onHide={() => setShowSupportModal(false)} centered>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary d-flex align-items-center">
                        <i className="bi bi-headset me-2"></i>
                        Suporte Técnico
                    </span>
                </Modal.Header>
                <Modal.Body className="p-0 support-modal-body">
                    <div className="support-chat">
                        <div className="support-chat-message">
                            <div className="support-chat-bubble">
                                <i className="bi bi-robot me-2"></i>
                                Olá! Como posso ajudá-lo hoje?
                            </div>
                            <span className="support-chat-meta">Suporte · Agora</span>
                        </div>
                        <div className="support-chat-message">
                            <div className="support-chat-bubble">
                                Escolha uma das opções abaixo para entrar em contato:
                            </div>
                        </div>
                        <div className="support-chat-options">
                            <button type="button" className="support-chat-option" onClick={() => copyToClipboard(emailContato, "email")}>
                                <span className="support-chat-option-icon support-chat-option-icon-email">
                                    <i className="bi bi-envelope-fill"></i>
                                </span>
                                <span className="support-chat-option-body">
                                    <span className="support-chat-option-title">Email</span>
                                    <span className="support-chat-option-subtitle">{emailContato}</span>
                                </span>
                                <i className="bi bi-copy support-chat-option-copy"></i>
                            </button>
                            {emailCopied && (
                                <div className="support-chat-success">
                                    <i className="bi bi-check-circle me-1"></i>
                                    Email copiado com sucesso!
                                </div>
                            )}
                            <button type="button" className="support-chat-option" onClick={() => copyToClipboard(telefoneContato, "whatsapp")}>
                                <span className="support-chat-option-icon support-chat-option-icon-whatsapp">
                                    <i className="bi bi-whatsapp"></i>
                                </span>
                                <span className="support-chat-option-body">
                                    <span className="support-chat-option-title">WhatsApp</span>
                                    <span className="support-chat-option-subtitle">{telefoneContato}</span>
                                </span>
                                <i className="bi bi-copy support-chat-option-copy"></i>
                            </button>
                            {whatsappCopied && (
                                <div className="support-chat-success">
                                    <i className="bi bi-check-circle me-1"></i>
                                    Número copiado com sucesso!
                                </div>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end w-100">
                        <button 
                            type="button"
                            className="btn btn-success w-100 d-flex align-items-center justify-content-center"
                            onClick={() => window.open(`https://wa.me/${whatsappContato}`)}
                        >
                            <i className="bi bi-whatsapp me-2"></i>
                            Chamar no WhatsApp
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            <style>{`
                .login-container {
                    min-height: 100vh;
                    background: linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-secondary) 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    position: relative;
                    overflow: hidden;
                }

                /* Elementos decorativos de fundo */
                .bg-decoration {
                    position: absolute;
                    inset: 0;
                    overflow: hidden;
                    pointer-events: none;
                }

                .bg-shape {
                    position: absolute;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.08);
                    animation: float 20s ease-in-out infinite;
                }

                .bg-shape-1 {
                    width: 400px;
                    height: 400px;
                    top: -100px;
                    left: -100px;
                    animation-delay: 0s;
                }

                .bg-shape-2 {
                    width: 300px;
                    height: 300px;
                    bottom: -50px;
                    right: -50px;
                    animation-delay: -5s;
                }

                .bg-shape-3 {
                    width: 200px;
                    height: 200px;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    animation-delay: -10s;
                }

                @keyframes float {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    25% { transform: translate(20px, -20px) scale(1.05); }
                    50% { transform: translate(-10px, 20px) scale(0.95); }
                    75% { transform: translate(-20px, -10px) scale(1.02); }
                }

                .login-content {
                    display: flex;
                    background: rgba(255, 255, 255, 0.97);
                    backdrop-filter: blur(20px);
                    border-radius: 24px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    overflow: hidden;
                    width: 100%;
                    max-width: 1100px;
                    position: relative;
                    z-index: 10;
                }

                .login-animation-container {
                    flex: 0 0 0%;
                    max-width: 0;
                    background: linear-gradient(135deg, color-mix(in srgb, var(--theme-primary) 10%, transparent) 0%, color-mix(in srgb, var(--theme-secondary) 10%, transparent) 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 0;
                    position: relative;
                    opacity: 0;
                    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
                    overflow: hidden;
                }

                .login-content.animated .login-animation-container {
                    flex: 0 0 50%;
                    max-width: 50%;
                    padding: 60px 40px;
                    opacity: 1;
                }

                .animation-wrapper {
                    width: 100%;
                    max-width: 420px;
                    opacity: 0;
                    transform: translateX(30px) scale(0.9);
                    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.4s;
                }

                .login-content.animated .animation-wrapper {
                    opacity: 1;
                    transform: translateX(0) scale(1);
                }

                .login-animation {
                    width: 100%;
                }

                .login-form-container {
                    flex: 1;
                    padding: 60px 50px;
                    max-width: 480px;
                    margin: 0 auto;
                    opacity: 0;
                    transform: translateY(20px);
                    animation: formFadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
                }

                @keyframes formFadeIn {
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .login-logo {
                    max-width: 160px;
                    margin-bottom: 20px;
                    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
                }

                .login-title {
                    font-weight: 700;
                    font-size: 1.5rem;
                    background: linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-secondary) 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    margin-bottom: 8px;
                }

                .login-subtitle {
                    color: #6b7280;
                    font-size: 0.95rem;
                    margin-bottom: 0;
                }

                .login-header {
                    margin-bottom: 35px;
                }

                /* Input Wrapper com ícone */
                .input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .input-icon {
                    position: absolute;
                    left: 18px;
                    color: #9ca3af;
                    font-size: 18px;
                    z-index: 2;
                    transition: color 0.3s ease;
                    display: flex;
                    align-items: center;
                }

                .input-wrapper:focus-within .input-icon {
                    color: var(--theme-primary);
                }

                .custom-input {
                    width: 100%;
                    border: 2px solid #e5e7eb;
                    border-radius: 14px;
                    padding: 16px 18px 16px 50px;
                    font-size: 15px;
                    transition: all 0.3s ease;
                    background-color: #f9fafb;
                }

                .custom-input-password {
                    padding-right: 50px;
                }

                .custom-input:focus {
                    border-color: var(--theme-primary);
                    box-shadow: 0 0 0 4px color-mix(in srgb, var(--theme-primary) 15%, transparent);
                    background-color: #fff;
                    outline: none;
                }

                .custom-input::placeholder {
                    color: #9ca3af;
                    font-size: 14px;
                }

                .password-toggle {
                    position: absolute;
                    right: 16px;
                    color: #9ca3af;
                    font-size: 18px;
                    border: none;
                    background: transparent;
                    padding: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    border-radius: 8px;
                }

                .password-toggle:hover {
                    color: var(--theme-primary);
                    background: color-mix(in srgb, var(--theme-primary) 10%, transparent);
                }

                .password-toggle:focus {
                    outline: none;
                }

                /* Validação - Estados de erro */
                .input-error {
                    border-color: #ef4444 !important;
                    background-color: #fef2f2 !important;
                }

                .input-error:focus {
                    border-color: #ef4444 !important;
                    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15) !important;
                }

                .input-icon.error {
                    color: #ef4444 !important;
                }

                .error-message {
                    display: flex;
                    align-items: center;
                    color: #ef4444;
                    font-size: 13px;
                    font-weight: 500;
                    margin-top: 8px;
                    padding-left: 4px;
                    animation: fadeInError 0.3s ease-out;
                }

                @keyframes fadeInError {
                    from {
                        opacity: 0;
                        transform: translateY(-5px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Animação de shake */
                .shake {
                    animation: shakeAnimation 0.5s ease-in-out;
                }

                @keyframes shakeAnimation {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
                    20%, 40%, 60%, 80% { transform: translateX(8px); }
                }

                /* Custom Checkbox */
                .custom-checkbox {
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    user-select: none;
                }

                .custom-checkbox input {
                    position: absolute;
                    opacity: 0;
                    cursor: pointer;
                }

                .checkmark {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #d1d5db;
                    border-radius: 6px;
                    margin-right: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    background: #fff;
                }

                .custom-checkbox input:checked ~ .checkmark {
                    background: linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-secondary) 100%);
                    border-color: transparent;
                }

                .custom-checkbox input:checked ~ .checkmark::after {
                    content: '';
                    width: 5px;
                    height: 10px;
                    border: solid white;
                    border-width: 0 2px 2px 0;
                    transform: rotate(45deg);
                    margin-bottom: 2px;
                }

                .checkbox-label {
                    color: #6b7280;
                    font-size: 14px;
                }

                .forgot-password-link {
                    background: none;
                    border: none;
                    color: var(--theme-primary);
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    padding: 0;
                    transition: color 0.2s ease;
                }

                .forgot-password-link:hover {
                    color: var(--theme-secondary);
                    text-decoration: underline;
                }

                /* Botão de Login */
                .btn-login {
                    width: 100%;
                    padding: 16px 24px;
                    background: linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-secondary) 100%);
                    border: none;
                    border-radius: 14px;
                    color: white;
                    font-weight: 600;
                    font-size: 16px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    overflow: hidden;
                }

                .btn-login::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
                    transition: left 0.5s ease;
                }

                .btn-login:hover:not(:disabled)::before {
                    left: 100%;
                }

                .btn-login:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 30px -10px color-mix(in srgb, var(--theme-primary) 50%, transparent);
                }

                .btn-login:active:not(:disabled) {
                    transform: translateY(0);
                }

                .btn-login:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .btn-login i {
                    transition: transform 0.3s ease;
                }

                .btn-login:hover:not(:disabled) i {
                    transform: translateX(4px);
                }

                /* Link do Validador */
                .validator-link {
                    background: none;
                    border: none;
                    color: #6b7280;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    padding: 10px 20px;
                    border-radius: 10px;
                    transition: all 0.2s ease;
                    display: inline-flex;
                    align-items: center;
                }

                .validator-link:hover {
                    color: var(--theme-primary);
                    background: color-mix(in srgb, var(--theme-primary) 8%, transparent);
                }

                .version-info {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 12px;
                    font-weight: 500;
                    z-index: 100;
                    background: rgba(255, 255, 255, 0.15);
                    padding: 6px 14px;
                    border-radius: 20px;
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }

                .login-footer {
                    position: fixed;
                    bottom: 20px;
                    left: 50%;
                    transform: translateX(-50%);
                    color: rgba(255, 255, 255, 0.9);
                    font-size: 13px;
                    text-align: center;
                    padding: 10px 24px;
                    max-width: calc(100% - 200px);
                    line-height: 1.5;
                    z-index: 100;
                    background: rgba(255, 255, 255, 0.12);
                    backdrop-filter: blur(10px);
                    border-radius: 30px;
                    border: 1px solid rgba(255, 255, 255, 0.18);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                    justify-content: center;
                }

                .footer-divider {
                    opacity: 0.5;
                }

                .footer-link {
                    color: #fff;
                    text-decoration: none;
                    font-weight: 500;
                    transition: opacity 0.2s ease;
                }

                .footer-link:hover {
                    opacity: 0.8;
                    color: #fff;
                }

                .footer-link-btn {
                    background: none;
                    border: none;
                    color: rgba(255, 255, 255, 0.9);
                    padding: 0;
                    font-size: 13px;
                    cursor: pointer;
                    transition: opacity 0.2s ease;
                }

                .footer-link-btn:hover {
                    opacity: 0.7;
                }

                /* Botão de Suporte */
                .support-icon {
                    position: fixed;
                    bottom: 30px;
                    right: 30px;
                    width: 58px;
                    height: 58px;
                    background: linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-secondary) 100%);
                    color: white;
                    border: none;
                    border-radius: 50%;
                    font-size: 22px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 8px 25px -5px color-mix(in srgb, var(--theme-primary) 50%, transparent);
                    transition: all 0.3s ease;
                    z-index: 1000;
                    cursor: pointer;
                }

                .support-icon:hover {
                    transform: translateY(-3px) scale(1.05);
                    box-shadow: 0 12px 35px -5px color-mix(in srgb, var(--theme-primary) 60%, transparent);
                }

                .support-pulse {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    background: linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-secondary) 100%);
                    animation: pulse-ring 2s ease-out infinite;
                    z-index: -1;
                }

                @keyframes pulse-ring {
                    0% {
                        transform: scale(1);
                        opacity: 0.6;
                    }
                    100% {
                        transform: scale(1.5);
                        opacity: 0;
                    }
                }

                /* Modal Suporte Técnico - formato chat */
                .support-modal-body {
                    overflow: hidden;
                }
                .support-chat {
                    padding: 1.25rem 1.5rem;
                    background: #f1f5f9;
                    max-height: 420px;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }
                .support-chat-message {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    max-width: 100%;
                }
                .support-chat-bubble {
                    background: #fff;
                    padding: 12px 16px;
                    border-radius: 16px 16px 16px 4px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
                    font-size: 0.9375rem;
                    line-height: 1.45;
                    color: #334155;
                    max-width: 100%;
                    border: 1px solid #e2e8f0;
                }
                .support-chat-meta {
                    font-size: 0.75rem;
                    color: #94a3b8;
                    margin-top: 4px;
                    margin-left: 2px;
                }
                .support-chat-options {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }
                .support-chat-option {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    text-align: left;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 14px 16px;
                    cursor: pointer;
                    transition: border-color 0.2s, box-shadow 0.2s;
                }
                .support-chat-option:hover {
                    border-color: var(--theme-primary);
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                }
                .support-chat-option-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.25rem;
                    color: #fff;
                    flex-shrink: 0;
                    margin-right: 14px;
                }
                .support-chat-option-icon-email {
                    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
                }
                .support-chat-option-icon-whatsapp {
                    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
                }
                .support-chat-option-body {
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }
                .support-chat-option-title {
                    font-weight: 600;
                    font-size: 0.9375rem;
                    color: #1e293b;
                }
                .support-chat-option-subtitle {
                    font-size: 0.8125rem;
                    color: #64748b;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .support-chat-option-copy {
                    color: #94a3b8;
                    font-size: 1.125rem;
                    flex-shrink: 0;
                    margin-left: 8px;
                }
                .support-chat-option:hover .support-chat-option-copy {
                    color: var(--theme-primary);
                }
                .support-chat-success {
                    color: #16a34a;
                    font-size: 0.8125rem;
                    font-weight: 500;
                    padding: 6px 0 0 4px;
                    display: flex;
                    align-items: center;
                    animation: supportFadeIn 0.3s ease-out;
                }
                @keyframes supportFadeIn {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Responsivo */
                @media (max-width: 991.98px) {
                    .login-container {
                        padding: 20px 15px;
                    }

                    .login-content {
                        flex-direction: column;
                        max-width: 460px;
                    }

                    .login-form-container {
                        padding: 40px 30px;
                    }

                    .login-logo {
                        max-width: 140px;
                        margin-bottom: 15px;
                    }

                    .support-icon {
                        width: 52px;
                        height: 52px;
                        font-size: 20px;
                        bottom: 20px;
                        right: 20px;
                    }

                    .version-info {
                        top: 15px;
                        right: 15px;
                        font-size: 11px;
                        padding: 5px 12px;
                    }

                    .login-footer {
                        bottom: 15px;
                        padding: 8px 20px;
                        max-width: calc(100% - 100px);
                        font-size: 12px;
                    }
                }

                @media (max-width: 576px) {
                    .login-container {
                        padding: 15px 12px;
                    }

                    .login-content {
                        border-radius: 20px;
                    }

                    .login-form-container {
                        padding: 35px 24px;
                    }

                    .version-info {
                        top: 12px;
                        right: 12px;
                        font-size: 10px;
                        padding: 4px 10px;
                    }

                    .login-footer {
                        bottom: 12px;
                        left: 12px;
                        right: 12px;
                        transform: none;
                        font-size: 11px;
                        padding: 10px 16px;
                        max-width: none;
                        width: auto;
                        border-radius: 20px;
                    }

                    .support-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 18px;
                        bottom: 75px;
                        right: 12px;
                    }

                    .custom-input {
                        padding: 14px 16px 14px 46px;
                    }

                    .btn-login {
                        padding: 14px 20px;
                    }

                    .bg-shape-1 {
                        width: 250px;
                        height: 250px;
                    }

                    .bg-shape-2 {
                        width: 200px;
                        height: 200px;
                    }

                    .bg-shape-3 {
                        width: 150px;
                        height: 150px;
                    }
                }
            `}</style>
        </>
    );
}

export default Login;